import { Input } from 'alurkerja-ui'
import { stringToTitleCase } from '@/utils/functions/stringToTitleCase'
import { BiSolidTrashAlt } from 'react-icons/bi'
import { FaPlus, FaChevronDown, FaChevronRight } from 'react-icons/fa'
import { useState } from 'react'

export type EditSelectOptionsProps = {
  currentElementState: any
  setCurrentElementState: React.Dispatch<any>
  element: string
}

export const EditSelectOptions = (props: EditSelectOptionsProps) => {
  const { currentElementState, setCurrentElementState, element } = props
  const [collapsedItems, setCollapsedItems] = useState<{ [key: number]: boolean }>({})

  const toggleCollapse = (index: number) => {
    setCollapsedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const isCheckbox = currentElementState?.['form_field_type'] === 'INPUT_CHECKBOX'
  const isInputSelect = currentElementState?.['form_field_type'] === 'INPUT_SELECT'
  const enableMultiple = (isInputSelect && currentElementState?.['multiple']) || isCheckbox
  const showCheckbox = isCheckbox || isInputSelect

  // Helper function to check if an option exists in defaultValue
  const optionExistsInDefaultValue = (prevState: any, option: any) => {
    if (isCheckbox) {
      return prevState['defaultValue']?.includes(option.key)
    } else {
      return prevState['defaultValue']?.some((item: any) => item.key === option.key)
    }
  }

  // Helper function to remove an option from defaultValue
  const removeOptionFromDefaultValue = (prevState: any, option: any) => {
    if (isCheckbox) {
      return prevState['defaultValue'].filter((key: string) => key !== option.key)
    } else {
      return prevState['defaultValue'].filter((item: any) => item.key !== option.key)
    }
  }

  // Helper function to add an option to defaultValue
  const addOptionToDefaultValue = (prevState: any, option: any) => {
    if (isCheckbox) {
      // For checkbox, just add the key to the array
      return [...(prevState['defaultValue'] || []), option.key]
    } else if (enableMultiple) {
      // For multiple select, add object with key and label
      return [
        ...(prevState['defaultValue'] || []),
        {
          key: option.key,
          label: option.label,
        },
      ]
    } else {
      // For single select, replace existing selection
      return [
        {
          key: option.key,
          label: option.label,
        },
      ]
    }
  }

  // Helper function to handle checkbox/select option changes
  const handleOptionChange = (prevState: any, option: any) => {
    const optionExists = optionExistsInDefaultValue(prevState, option)

    if (optionExists) {
      // If option exists, remove it
      return {
        ...prevState,
        ['defaultValue']: removeOptionFromDefaultValue(prevState, option),
      }
    } else {
      // If option doesn't exist, add it
      return {
        ...prevState,
        ['defaultValue']: addOptionToDefaultValue(prevState, option),
      }
    }
  }

  return (
    <div className="flex flex-col py-2 px-3">
      <div className="flex flex-col space-y-2">
        <div className="flex flex-col justify-center  rounded-lg py-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-1">
              <span className="font-semibold">{stringToTitleCase(element)}</span>
              <p className="text-sm">({currentElementState?.[element]?.options?.length})</p>
            </div>
            <div className="flex space-x-2 items-center">
              <button
                className="p-1 px-2 bg-main-blue-alurkerja text-white rounded"
                onClick={() => {
                  setCurrentElementState((prevState: any) => {
                    return {
                      ...prevState,
                      [element]: {
                        ...prevState[element],
                        options: [
                          ...prevState[element].options,
                          {
                            key: 'key-' + prevState[element].options.length,
                            label: 'label-' + prevState[element].options.length,
                          },
                        ],
                      },
                    }
                  })
                }}
              >
                <div className="flex items-center space-x-1">
                  <span className="text-xs">Add Option</span>
                  <FaPlus size={12} />
                </div>
              </button>
            </div>
          </div>
          <ul className="list-inside">
            {currentElementState?.[element]?.options?.map((option: { key: string; label: string }, index: number) => {
              const isCollapsed = collapsedItems[index]
              return (
                <li key={index} className="bg-slate-50 rounded-md overflow-hidden mb-2">
                  <div className="bg-slate-200 rounded-md w-full p-1 flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => toggleCollapse(index)} className="p-1 hover:bg-slate-300 rounded">
                        {isCollapsed ? <FaChevronRight size={12} /> : <FaChevronDown size={12} />}
                      </button>
                      <span>{option.key ?? ''}</span>
                    </div>
                    <button
                      className="p-1 bg-red-alurkerja text-white rounded"
                      onClick={() => {
                        setCurrentElementState((prevState: any) => {
                          const newOptions = [...prevState[element].options]
                          newOptions.splice(index, 1)
                          return {
                            ...prevState,
                            [element]: {
                              ...prevState[element],
                              options: newOptions,
                            },
                          }
                        })
                      }}
                    >
                      <BiSolidTrashAlt size={14} />
                    </button>
                  </div>
                  {!isCollapsed && (
                    <div className="py-2 text-sm">
                      <div className="grid grid-cols-4 px-4 items-center mb-1">
                        <label htmlFor={'label' + index}>Label</label>
                        <Input
                          id={'label' + index}
                          className="w-full h-8 text-sm col-span-3"
                          type="text"
                          size="xs"
                          value={option.label}
                          onChange={(e) => {
                            setCurrentElementState((prevState: any) => {
                              const newArray = [...prevState[element].options]
                              newArray[index] = { ...newArray[index], label: e.target.value }
                              return {
                                ...prevState,
                                [element]: {
                                  ...prevState[element],
                                  options: newArray,
                                },
                              }
                            })
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-4 px-4 items-center">
                        <label htmlFor={'label' + index}>Value</label>
                        <Input
                          id={'label' + index}
                          className="w-full h-8 text-sm col-span-3"
                          type="text"
                          size="xs"
                          value={option.key}
                          onChange={(e) => {
                            setCurrentElementState((prevState: any) => {
                              const newArray = [...prevState[element].options]
                              newArray[index] = { ...newArray[index], key: e.target.value }
                              return {
                                ...prevState,
                                [element]: {
                                  ...currentElementState[element],
                                  options: newArray,
                                },
                              }
                            })
                          }}
                        />
                      </div>

                      {showCheckbox && (
                        <div className="grid grid-cols-4 px-4 items-center mt-2">
                          <label htmlFor={'selected' + index}>Selected</label>
                          <input
                            id={'selected' + index}
                            className="h-4 w-4 text-sm col-span-3"
                            type="checkbox"
                            checked={
                              isCheckbox
                                ? currentElementState?.['defaultValue']?.includes(option.key)
                                : currentElementState?.['defaultValue']?.some((item: any) => item.key === option.key)
                            }
                            onChange={() => {
                              setCurrentElementState((prevState: any) => handleOptionChange(prevState, option))
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}
